import { Dispatch, SetStateAction, useMemo } from "react";
import { SALES_STATUS_COLOR, getStatusLabel } from "../../data";
import { formatNumber } from "../../../../utils";
import { CashSalesResponse } from "../../../../types/sale.type";
import { COMPACT_QUERY } from "../../../../utils/hooks/useCompact";
import { formatDate, formatThaiFullDate } from "../../../../utils/date";
import RejectCancelLink from "../../CancelActions/RejectCancelLink";
import RevertCancelLink from "../../CancelActions/RevertCancelLink";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import StorefrontIcon from "@mui/icons-material/Storefront";
// import EditIcon from "@mui/icons-material/Edit";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
// import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
// import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import CashSalesDetailsTable from "./Table";
import BillTotals from "../../BillTotals";
import ReplacedByInfo from "../../ReplacedByInfo";

const compact = "@media " + COMPACT_QUERY;

interface DetailsDocumentProps {
  cashSales: CashSalesResponse;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function DetailsDocument({
  cashSales,
  setTriggerFetch,
}: DetailsDocumentProps) {
  const address = useMemo(() => {
    return [
      cashSales?.customer_address1,
      cashSales?.customer_address2,
      cashSales?.customer_street,
      cashSales?.customer_subdistrict,
      cashSales?.customer_district,
      cashSales?.customer_province,
      cashSales?.customer_zipcode,
    ]
      .filter(Boolean)
      .join(" ");
  }, [
    cashSales?.customer_address1,
    cashSales?.customer_address2,
    cashSales?.customer_street,
    cashSales?.customer_subdistrict,
    cashSales?.customer_district,
    cashSales?.customer_province,
    cashSales?.customer_zipcode,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          [compact]: { flexWrap: "wrap", gap: 1 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" fontWeight={600}>
            ใบเสนอราคา
          </Typography>
          <Typography fontWeight={600} color="text.secondary">
            เลขที่ {cashSales.order_no}
          </Typography>
          <ReplacedByInfo replacedBy={cashSales.replaced_by} variant="body2" />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            [compact]: { flexWrap: "wrap", justifyContent: "flex-end" },
          }}
        >
          {cashSales.updated_at && (
            <Typography
              fontWeight={600}
              variant="caption"
              color="text.secondary"
            >
              แก้ไขเมื่อ {formatThaiFullDate(cashSales.updated_at)}
              {/* แก้ไขครั้งที่ 1 เมื่อ {formatThaiFullDate(cashSales.updated_at)} */}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: SALES_STATUS_COLOR[cashSales.status] + "1A",
              color: SALES_STATUS_COLOR[cashSales.status],
              borderRadius: 4,
              p: 1,
              fontWeight: 600,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: SALES_STATUS_COLOR[cashSales.status],
                borderRadius: "50%",
                mr: 1,
              }}
            />
            {getStatusLabel(cashSales.status)}
          </Box>
          <RejectCancelLink
            uuid={cashSales.uuid}
            status={cashSales.status}
            setTriggerFetch={setTriggerFetch}
          />
          <RevertCancelLink
            uuid={cashSales.uuid}
            status={cashSales.status}
            setTriggerFetch={setTriggerFetch}
          />
        </Box>
      </Box>
      <Divider sx={{ my: 2, [compact]: { my: 1.5 } }} />
      <Box sx={{ display: "flex", gap: 1, mb: 2, [compact]: { mb: 1.5 } }}>
        <StorefrontIcon />
        <Typography fontWeight={600} variant="body1">
          ข้อมูลลูกค้า
        </Typography>
        {/* <EditIcon
          fontSize="small"
          color="primary"
          sx={{ cursor: "pointer", ml: 2 }}
        /> */}
      </Box>
      <Grid container spacing={1}>
        <Grid size={5}>
          <Typography fontWeight={600} variant="body1" color="text.secondary">
            ร้าน
          </Typography>
        </Grid>
        <Grid size={3}>
          <Typography fontWeight={600} variant="body1" color="text.secondary">
            รหัสร้านค้า
          </Typography>
        </Grid>
        <Grid size={4}>
          <Typography fontWeight={600} variant="body1" color="text.secondary">
            วันที่ออกเอกสาร
          </Typography>
        </Grid>
        <Grid size={5}>
          <Typography fontWeight={500} variant="body1">
            {cashSales.customer_name} <br />
            {address}
          </Typography>
        </Grid>
        <Grid size={3}>
          <Typography fontWeight={500} variant="body1">
            {cashSales.customer_code}
          </Typography>
        </Grid>
        <Grid size={4}>
          <Typography fontWeight={500} variant="body1">
            {formatDate(cashSales.order_date)}
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3, [compact]: { my: 1.5 } }} />
      <Box sx={{ display: "flex", gap: 6, [compact]: { gap: 3, flexWrap: "wrap" } }}>
        <Box sx={{ display: "flex", gap: 1, mb: 3, [compact]: { mb: 1.5 } }}>
          <SellOutlinedIcon />
          <Typography fontWeight={600} variant="body1">
            ข้อมูลการเสียภาษี
          </Typography>
        </Box>
        {!cashSales.inv_taxid ? (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography fontWeight={500} variant="body1">
              ไม่มี
            </Typography>
            <Typography variant="body1" color="error">
              (ไม่ต้องส่ง E-Tax)
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography fontWeight={500} variant="body1">
                เลขที่ผู้เสียภาษี
              </Typography>
              <Typography fontWeight={500} variant="body1">
                {cashSales.inv_taxid}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography fontWeight={500} variant="body1">
                อัตราภาษี {parseFloat(cashSales.tax_rate)}%
              </Typography>
              <Typography fontWeight={500} variant="body1">
                จำนวนเงิน {formatNumber(cashSales.tax_amount)} บาท
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3, [compact]: { mb: 1.5 } }} />
      {/* <Box sx={{ display: "flex", gap: 10 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <FileOpenOutlinedIcon />
          <Typography fontWeight={600} variant="body1">
            ข้อมูลเพิ่มเติม
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 0.5, cursor: "pointer" }}>
            <Typography
              fontWeight={500}
              variant="body1"
              color="link"
              sx={{ textDecoration: "underline" }}
            >
              QT123499595
            </Typography>
            <CallMadeOutlinedIcon
              sx={{ color: (theme) => theme.palette.link.main }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, cursor: "pointer" }}>
            <Typography
              fontWeight={500}
              variant="body1"
              color="link"
              sx={{ textDecoration: "underline" }}
            >
              QT123499595
            </Typography>
            <CallMadeOutlinedIcon
              sx={{ color: (theme) => theme.palette.link.main }}
            />
          </Box>
        </Box>
      </Box> */}
      {/* <Divider sx={{ my: 3 }} /> */}
      <Typography
        fontWeight={600}
        variant="body1"
        mb={2}
        sx={{ [compact]: { mb: 1 } }}
      >
        รายการ
      </Typography>
      <CashSalesDetailsTable cashSalesDetails={cashSales?.order_detail} />
      <BillTotals
        totalAmount={cashSales?.total_amount}
        discountAmount={cashSales?.discount_amount}
        totalNoTax={cashSales?.total_no_tax}
        taxRate={cashSales?.tax_rate}
        taxAmount={cashSales?.tax_amount}
        grandTotal={cashSales?.grand_total}
      />
    </>
  );
}
